import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from '@getro/rombo';
import { Text, Flex, Box } from 'rebass/styled-components';
import DynamicTooltip from 'components/atoms/dynamicTooltip';
import { Circle } from 'lucide-react';
import { BoostIcon } from './BoostIcon';
import { trackEvent } from '../../../../helpers/analytics';

const useJobBoostStatus = (currentJob, onLaunchApplicationBoostAgent) =>
  useMemo(() => {
    const jobBoostStatuses = [
      {
        id: 'not_boosted',
        label: 'Not boosted',
        background: 'neutral.20',
        actionText: 'Boost job',
        action: (job) => {
          trackEvent('application_boost:boost_job_click');
          onLaunchApplicationBoostAgent({ view: 'launch', job });
        },
      },
      {
        id: 'ready_to_launch',
        label: 'Ready to launch',
        color: 'blue.700',
        background: '#EDF0FD',
        actionText: 'Open agent',
        action: (job) => {
          trackEvent('application_boost:open_boost_agent', {
            agent: 'application_boost',
          });
          onLaunchApplicationBoostAgent({ view: 'boost', job });
        },
      },
      {
        id: 'needs_calibration',
        label: 'Needs calibration',
        color: 'blue.700',
        background: '#EDF0FD',
        actionText: 'Calibrate',
        action: (job) => {
          trackEvent('application_boost:open_boost_agent', {
            agent: 'calibration-selection',
          });
          onLaunchApplicationBoostAgent({ view: 'calibration-selection', job });
        },
      },
      {
        id: 'calibrating',
        label: 'Preparing boost',
        color: 'text.main',
        background: 'neutral.100',
        actionText: null,
        action: null,
        tooltip: "We're getting ready to boost your job. It'll be ready in a few minutes.",
      },
      {
        id: 'in_progress',
        label: 'In progress',
        icon: BoostIcon,
        color: '',
        background: 'purple.100',
        actionText: 'Open agent',
        sx: {
          display: 'flex',
          flexShrink: 0,
          alignItems: 'center',
          gap: '8px',
          borderRadius: '4px',
          background:
            'linear-gradient(78deg, rgba(73, 0, 255, 0.08) 0%, rgba(212, 0, 255, 0.08) 50%, rgba(255, 146, 87, 0.08) 90%, rgba(73, 0, 255, 0.08) 100%), #FFF',
          backgroundSize: '200% 200%',
          animation: 'gradient 2.5s ease-in infinite',
          px: '8px',
          height: '24px',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: '4px',
            padding: '1px',
            background: 'linear-gradient(78deg, #4900FF 0%, #D400FF 50%, #FF9257 90%, #4900FF 100%), #FFF',
            backgroundSize: '200% 200%',
            animation: 'gradient 2.5s ease-in infinite',
            mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            maskComposite: 'exclude',
            overflow: 'hidden',
          },
          '@keyframes gradient': {
            '0%': {
              backgroundPosition: '200% 50%',
            },
            '100%': {
              backgroundPosition: '0% 50%',
            },
          },
        },
        action: (job) => {
          trackEvent('application_boost:open_boost_agent_click', {
            agent: 'application_boost',
          });
          onLaunchApplicationBoostAgent({ view: 'boost', job });
        },
      },
      {
        id: 'finished',
        label: 'Completed',
        color: 'green.700',
        background: 'green.20',
        actionText: 'Open agent',
        action: (job) => {
          onLaunchApplicationBoostAgent({ view: 'boost', job });
        },
      },
    ];
    return jobBoostStatuses.find((status) => status.id === currentJob.sourcingCampaign?.status) ?? jobBoostStatuses[0];
  }, [currentJob, onLaunchApplicationBoostAgent]);

export const ApplicationBoostCell = ({ job, onLaunchApplicationBoostAgent }) => {
  const jobBoostStatus = useJobBoostStatus(job, onLaunchApplicationBoostAgent);

  const shouldExcludeForOurAgents = ['executive', 'cxo', 'vice_president'].includes(job.seniority);
  const isJobNotVisibleOrHidden = ['hidden', 'not_visible', 'hidden_forced'].includes(job.visibility);

  return (
    <Flex alignItems="center" sx={{ gap: '8px' }} color="text.main">
      {isJobNotVisibleOrHidden && (
        <Box
          sx={{
            color: 'text.subtle',
            fontSize: '14px',
            fontWeight: '400',
            lineHeight: '24px',
            fontStyle: 'italic',
          }}
        >
          Not available for non-visible jobs
        </Box>
      )}
      {!isJobNotVisibleOrHidden && shouldExcludeForOurAgents && (
        <Tooltip
          content="AI boosting works best for roles up to Director level. Executive roles require a more tailored approach."
          position="top-start"
          trigger={
            <Box
              sx={{
                color: 'text.subtle',
                fontSize: '14px',
                fontWeight: '400',
                lineHeight: '24px',
                fontStyle: 'italic',
              }}
            >
              Not eligible
            </Box>
          }
        />
      )}
      {!isJobNotVisibleOrHidden && !shouldExcludeForOurAgents && (
        <DynamicTooltip
          useTooltip={Boolean(jobBoostStatus.tooltip)}
          content={jobBoostStatus.tooltip}
          position="top-start"
          sx={{
            '.tooltip-arrow': {
              left: '-50px !important',
            },
          }}
        >
          <Flex alignItems="center" sx={{ gap: '8px' }}>
            {jobBoostStatus.id === 'not_boosted' && (
              <Flex
                alignItems="center"
                sx={{ gap: '4px', cursor: 'pointer', textDecoration: 'underline', color: 'text.dark' }}
                onClick={() => {
                  if (jobBoostStatus?.action) {
                    jobBoostStatus?.action(job);
                  }
                }}
              >
                Boost job
                <Box
                  width="16px"
                  height="16px"
                  sx={{
                    flexShrink: 0,
                  }}
                >
                  <BoostIcon />
                </Box>
              </Flex>
            )}
            {jobBoostStatus.id === 'in_progress' && (
              <>
                <Box sx={jobBoostStatus.sx}>
                  <Text
                    as="span"
                    fontSize="14px"
                    color="text.dark"
                    sx={{
                      fontWeight: '500',
                      lineHeight: '24px',
                    }}
                  >
                    {jobBoostStatus.label}
                  </Text>
                  <BoostIcon />
                </Box>
                <Box
                  alignItems="center"
                  sx={{
                    opacity: 0,
                    position: 'relative',
                    top: '0',
                    left: '-24px',
                    transition: 'all 0.3s ease-in-out',
                    '.ag-row-hover &': {
                      opacity: 1,
                      top: '0',
                      left: 0,
                    },
                  }}
                >
                  <Text
                    sx={{
                      lineHeight: '24px',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      color: 'text.dark',
                    }}
                    onClick={() => {
                      if (jobBoostStatus?.action) {
                        jobBoostStatus?.action(job);
                      }
                    }}
                    fontSize="14px"
                  >
                    {jobBoostStatus?.actionText}
                  </Text>
                </Box>
              </>
            )}
            {jobBoostStatus.id === 'calibrating' && (
              <Box
                sx={{
                  display: 'flex',
                  flexShrink: 0,
                  alignItems: 'center',
                  gap: '4px',
                  borderRadius: '4px',
                  bg: jobBoostStatus.background || 'neutral.20',
                  px: '8px',
                  height: '24px',
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="6" height="6" viewBox="0 0 6 6" fill="none">
                  <Box
                    as={Circle}
                    size={6}
                    sx={{
                      color: jobBoostStatus.color,
                      background: jobBoostStatus.color,
                      fill: jobBoostStatus.color,
                    }}
                  />
                </svg>
                <Text
                  as="span"
                  fontSize="14px"
                  color={jobBoostStatus.color || 'text.main'}
                  sx={{
                    fontWeight: '500',
                    lineHeight: '24px',
                  }}
                >
                  {jobBoostStatus.label}
                </Text>
              </Box>
            )}
            {!['not_boosted', 'in_progress', 'calibrating'].includes(jobBoostStatus.id) && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    flexShrink: 0,
                    alignItems: 'center',
                    gap: '4px',
                    borderRadius: '4px',
                    bg: jobBoostStatus.background || 'neutral.20',
                    px: '8px',
                    height: '24px',
                    ...(jobBoostStatus.sx ? jobBoostStatus.sx : {}),
                  }}
                >
                  {jobBoostStatus.id !== 'finished' && (
                    <svg xmlns="http://www.w3.org/2000/svg" width="6" height="6" viewBox="0 0 6 6" fill="none">
                      <circle cx="3" cy="3" r="3" fill="#0F388A" />
                    </svg>
                  )}
                  <Text
                    as="span"
                    fontSize="14px"
                    color={jobBoostStatus.color || 'text.main'}
                    sx={{
                      fontWeight: '500',
                      lineHeight: '24px',
                    }}
                  >
                    {jobBoostStatus.label}
                  </Text>
                </Box>

                <Box
                  alignItems="center"
                  sx={{
                    opacity: 0,
                    position: 'relative',
                    left: '-24px',
                    transition: 'all 0.3s ease-in-out',
                    '.ag-row-hover &': {
                      opacity: 1,
                      left: 0,
                    },
                  }}
                >
                  <Text
                    sx={{
                      lineHeight: '24px',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      color: 'text.dark',
                    }}
                    onClick={() => {
                      if (jobBoostStatus?.action) {
                        jobBoostStatus?.action(job);
                      }
                    }}
                    fontSize="14px"
                  >
                    {jobBoostStatus?.actionText}
                  </Text>
                </Box>
              </>
            )}
          </Flex>
        </DynamicTooltip>
      )}
    </Flex>
  );
};

ApplicationBoostCell.propTypes = {
  job: PropTypes.object.isRequired,
  onLaunchApplicationBoostAgent: PropTypes.func.isRequired,
};
